/**
 * DriveGo — eneste backend for E Service-hjemmesiden.
 *
 * Kontrakt: `${REACT_APP_DRIVEGO_API}/api/public/v1/shop/${REACT_APP_DRIVEGO_TENANT}`
 *   GET  /catalog
 *   GET  /availability?date=YYYY-MM-DD&items=id1,id2
 *   GET  /availability-range?from=YYYY-MM-DD&to=YYYY-MM-DD&items=id1,id2
 *   POST /bookings
 *   POST /quote-requests
 * Svar: `{ data }` eller `{ error: { code, message } }`. CORS uden credentials.
 *
 * Funktionerne her mapper DriveGo-formatet til de datastrukturer, komponenterne
 * allerede brugte (service.price, service.duration_minutes, service.quote_required
 * osv.), så designet er uændret.
 *
 * Lokal udvikling uden DriveGo: sæt REACT_APP_DRIVEGO_MOCK=true (se drivego.mock.js).
 */
const API_ROOT = (process.env.REACT_APP_DRIVEGO_API || "https://app.drivego.dk").replace(/\/+$/, "");
const TENANT = process.env.REACT_APP_DRIVEGO_TENANT || "servicepro";
const USE_MOCK = process.env.REACT_APP_DRIVEGO_MOCK === "true";

const BASE = `${API_ROOT}/api/public/v1/shop/${encodeURIComponent(TENANT)}`;

/** Link til DriveGo-kundeportalen (erstatter «Log ind» / «Mine bestillinger»). */
export const PORTAL_URL = `${API_ROOT}/portal`;

export const TIMEZONE = "Europe/Copenhagen";

const FALLBACK_MESSAGES = {
  VALIDATION_ERROR: "Nogle af oplysningerne er ikke gyldige — tjek felterne og prøv igen.",
  NOT_FOUND: "Det du leder efter findes ikke længere.",
  SLOT_TAKEN: "Tiden er netop blevet booket af en anden — vælg venligst en anden tid.",
  RATE_LIMITED: "Der er sendt for mange forespørgsler på kort tid — vent et øjeblik og prøv igen.",
  NETWORK_ERROR: "Kunne ikke få forbindelse — tjek din internetforbindelse og prøv igen.",
  UNKNOWN: "Noget gik galt — prøv igen, eller ring til os.",
};

export class DriveGoError extends Error {
  constructor(code, message, status) {
    super(message || FALLBACK_MESSAGES[code] || FALLBACK_MESSAGES.UNKNOWN);
    this.name = "DriveGoError";
    this.code = code || "UNKNOWN";
    this.status = status;
  }
}

/** Dansk, kundevendt fejltekst for en hvilken som helst fejl. */
export const errorMessage = (err) => {
  if (err instanceof DriveGoError) {
    // Backend-beskeder kan være tekniske; brug vores egne tekster for kendte koder
    // undtagen VALIDATION_ERROR, hvor backendens besked typisk peger på feltet.
    if (err.code === "VALIDATION_ERROR" && err.message) return err.message;
    return FALLBACK_MESSAGES[err.code] || err.message || FALLBACK_MESSAGES.UNKNOWN;
  }
  return FALLBACK_MESSAGES.UNKNOWN;
};

async function request(path, { method = "GET", body } = {}) {
  const url = `${BASE}${path}`;
  const init = {
    method,
    credentials: "omit",
    headers: body ? { "Content-Type": "application/json", Accept: "application/json" } : { Accept: "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  };

  let res;
  try {
    if (USE_MOCK) {
      // Dynamisk import: mocken kommer kun med i bundlen, når mock er slået til.
      const { mockFetch } = await import("@/lib/drivego.mock");
      res = await mockFetch(path, init);
    } else {
      res = await fetch(url, init);
    }
  } catch {
    throw new DriveGoError("NETWORK_ERROR", null, 0);
  }

  let json = null;
  try {
    json = await res.json();
  } catch {
    /* tomt eller ugyldigt svar */
  }

  if (!res.ok || !json || json.error) {
    const code = json?.error?.code || (res.status === 429 ? "RATE_LIMITED" : res.status === 404 ? "NOT_FOUND" : "UNKNOWN");
    throw new DriveGoError(code, json?.error?.message, res.status);
  }
  return json.data;
}

// ---------------------------------------------------------------------------
// Katalog
// ---------------------------------------------------------------------------

// Billeder vi selv hoster ligger i public/img og registreres i DriveGo som
// https://eservice-jylland.dk/img/… — gør dem relative, så de også virker på
// Pages-preview (*.pages.dev) og før domænet er flyttet.
const OWN_IMAGE_RE = /^https:\/\/(www\.)?eservice-jylland\.dk(?=\/img\/)/i;
const localizeImage = (url) => (url ? url.replace(OWN_IMAGE_RE, "") : null);

/** DriveGo-item → det "service"-objekt komponenterne bruger. */
export const mapItem = (item, categoryNames = {}) => ({
  id: item.id,
  name: item.name || "",
  description: item.description || "",
  price: Number(item.priceInclVat ?? 0),
  duration_minutes: Number(item.durationMinutes ?? 0),
  category: categoryNames[item.category] || item.category || "",
  category_key: item.category || null,
  image_url: localizeImage(item.imageUrl),
  is_popular: !!item.isPopular,
  quote_required: !!item.quoteOnly,
});

const mapCatalog = (data) => {
  const categories = [...(data?.categories || [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );
  const categoryNames = Object.fromEntries(categories.map((c) => [c.key, c.name]));
  return {
    tenant: data?.tenant || {},
    categories,
    services: (data?.items || []).map((i) => mapItem(i, categoryNames)),
  };
};

let catalogPromise = null;

/**
 * Henter kataloget én gang pr. sidevisning (deles mellem forside, booking og footer).
 * Returnerer `{ tenant, categories, services }`.
 */
export const getCatalog = () => {
  if (!catalogPromise) {
    catalogPromise = request("/catalog").then(mapCatalog).catch((err) => {
      catalogPromise = null; // tillad nyt forsøg
      throw err;
    });
  }
  return catalogPromise;
};

// ---------------------------------------------------------------------------
// Ledige tider
// ---------------------------------------------------------------------------

const timeFmt = new Intl.DateTimeFormat("da-DK", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: TIMEZONE,
});

/** ISO-tidspunkt → "08:30" i dansk tid. */
export const formatSlotTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return timeFmt.format(d).replace(".", ":");
};

/**
 * Ledige starttider på en dato for de valgte ydelser.
 * Returnerer `{ date, durationMinutes, slots: [{ id, start, end, start_time }] }`.
 */
export const getAvailability = async (date, itemIds) => {
  const params = new URLSearchParams({ date, items: (itemIds || []).join(",") });
  const data = await request(`/availability?${params.toString()}`);
  return {
    date: data?.date || date,
    durationMinutes: data?.durationMinutes ?? 0,
    slots: (data?.slots || []).map((s) => ({
      id: s.start,
      start: s.start,
      end: s.end,
      start_time: formatSlotTime(s.start),
    })),
  };
};

/**
 * Hvilke dage i [from, to] (maks 31 dage) har mindst én ledig tid — ét kald i stedet
 * for ét pr. dag. Returnerer `{ [yyyy-MM-dd]: boolean }`.
 */
export const getAvailabilityRange = async (from, to, itemIds) => {
  const params = new URLSearchParams({ from, to, items: (itemIds || []).join(",") });
  const data = await request(`/availability-range?${params.toString()}`);
  return Object.fromEntries((data?.days || []).map((d) => [d.date, !!d.hasSlots]));
};

// ---------------------------------------------------------------------------
// Booking og tilbud
// ---------------------------------------------------------------------------

const clean = (v) => (typeof v === "string" ? v.trim() : v);
const optional = (v) => {
  const c = clean(v);
  return c ? c : undefined;
};

/**
 * «Vestergade 10, 8000 Aarhus C» → `{ address, postalCode, city }`, som DriveGo
 * kræver (serviceAddress er et objekt — ikke en tekststreng). Returnerer null,
 * hvis der ikke kan findes et dansk postnummer (4 cifre) efterfulgt af en by.
 */
export const parseServiceAddress = (value) => {
  const m = String(value || "").trim().match(/^(.+?)[,\s]+(\d{4})\s+(.+)$/);
  if (!m) return null;
  const address = m[1].replace(/[,\s]+$/, "").trim();
  const city = m[3].trim();
  return address && city ? { address, postalCode: m[2], city } : null;
};

/** Adressen som DriveGo kræver: separate felter (formularen) eller én tekststreng (ældre kald). */
const toServiceAddress = (value) => {
  if (value && typeof value === "object") {
    const address = clean(value.address);
    const postalCode = clean(value.postalCode);
    const city = clean(value.city);
    return address && postalCode && city ? { address, postalCode, city } : undefined;
  }
  return parseServiceAddress(value) || undefined;
};

/**
 * Opret booking-anmodning. Alle bookinger godkendes manuelt i DriveGo.
 * Returnerer `{ bookingId, status, paymentRequired, paymentUrl?, paymentExpiresAt? }`:
 * er kunden IKKE til stede og har værkstedet Stripe, svarer DriveGo
 * `paymentRequired: true` + et Stripe-betalingslink (status 'awaiting_payment').
 */
export const createBooking = ({
  items,
  start,
  customer,
  licensePlate,
  serviceAddress,
  customerPresent,
  keyLocation,
  notes,
  acceptTerms,
  waiveWithdrawal,
  website,
}) =>
  request("/bookings", {
    method: "POST",
    body: {
      items,
      start,
      customer: {
        name: clean(customer.name),
        phone: clean(customer.phone),
        email: clean(customer.email),
        address: optional(customer.address),
        postalCode: optional(customer.postalCode),
        city: optional(customer.city),
      },
      vehicle: { licensePlate: clean(licensePlate).toUpperCase() },
      serviceAddress: toServiceAddress(serviceAddress),
      customerPresent: !!customerPresent,
      keyLocation: customerPresent ? undefined : optional(keyLocation),
      notes: optional(notes),
      acceptTerms: acceptTerms === true,
      waiveWithdrawal: !!waiveWithdrawal,
      website: website || "",
    },
  });

/**
 * Kunden afbrød betalingen hos Stripe → frigiv tiden med det samme (ellers
 * frigives den automatisk, når betalingslinket udløber).
 */
export const abandonBooking = (bookingId, token) =>
  request(`/bookings/${encodeURIComponent(bookingId)}/abandon`, {
    method: "POST",
    body: { token: token || "" },
  });

/** Opret tilbudsforespørgsel (privat eller erhverv). */
export const createQuoteRequest = ({
  description,
  customer,
  licensePlate,
  isBusiness = false,
  companyName,
  cvr,
  website,
}) => {
  const plate = optional(licensePlate);
  return request("/quote-requests", {
    method: "POST",
    body: {
      description: clean(description),
      customer: {
        name: clean(customer.name),
        phone: clean(customer.phone),
        email: clean(customer.email),
        address: optional(customer.address),
        postalCode: optional(customer.postalCode),
        city: optional(customer.city),
      },
      // Erhvervsformularen har ingen nummerplade — se rapport/afvigelser.
      vehicle: plate ? { licensePlate: plate.toUpperCase() } : undefined,
      isBusiness: !!isBusiness,
      companyName: optional(companyName),
      cvr: optional(cvr),
      website: website || "",
    },
  });
};

/** Byg en læsbar beskrivelse til tilbudsforespørgslen af de felter, kontrakten ikke har. */
export const buildQuoteDescription = (lines) =>
  lines
    .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== "")
    .map(([label, value]) => (label ? `${label}: ${String(value).trim()}` : String(value).trim()))
    .join("\n");
